import React, { useState } from 'react';
import { 
  X, BarChart3, Users, Phone, Clock, TrendingUp, Target, 
  Play, Pause, Square, Settings, Download, ExternalLink,
  Calendar, MapPin, Activity, Zap, AlertCircle, CheckCircle,
  RefreshCw, Maximize2, Eye, Share2, Edit, Copy
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useClient } from '../contexts/ClientContext';
import TabSystem from './TabSystem';
import '../styles/campaign-details-modal.css';

const CampaignDetailsModal = ({ campaign, isOpen, onClose }) => {
  const { t, currentLanguage, renderKey } = useLanguage();
  const { clientConfig } = useClient();

  // Export functions with improved download handling
  const downloadFile = (content, filename, contentType) => {
    try {
      // Method 1: Blob + URL.createObjectURL (modern browsers)
      const blob = new Blob([content], { type: contentType });
      
      // Check if browser supports download
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // IE/Edge
        window.navigator.msSaveOrOpenBlob(blob, filename);
        return true;
      } else {
        // Modern browsers
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        // Set all attributes before appending
        link.href = url;
        link.download = filename;
        link.style.position = 'absolute';
        link.style.left = '-10000px';
        
        // Append, click, and clean up
        document.body.appendChild(link);
        
        // Force click with timeout to ensure DOM is ready
        setTimeout(() => {
          link.click();
          
          // Clean up after a short delay
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, 100);
        }, 50);
        
        return true;
      }
    } catch (error) {
      console.error('Download failed:', error);
      
      // Fallback: try data URL method
      try {
        const dataUrl = 'data:' + contentType + ';charset=utf-8,' + encodeURIComponent(content);
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return true;
      } catch (fallbackError) {
        console.error('Fallback download failed:', fallbackError);
        return false;
      }
    }
  };

  const exportToCsv = (campaign) => {
    const csvData = [
      // Headers with UTF-8 BOM for proper encoding
      ['Campo', 'Valor'],
      ['Nombre de Campaña', campaign.name],
      ['Tipo', getTypeLabel(campaign.type)],
      ['Estado', campaign.status === 'active' ? t('campaignDetails.status.active') : 
                 campaign.status === 'paused' ? t('campaignDetails.status.paused') : 
                 t('campaignDetails.status.completed')],
      ['Contactos Totales', campaign.contacts?.toLocaleString() || '0'],
      ['Contactos Realizados', campaign.completed?.toLocaleString() || '0'],
      ['Progreso (%)', `${campaign.progress || 0}%`],
      ['Tasa de Éxito (%)', `${campaign.successRate || 0}%`],
      ['Duración Promedio', campaign.avgDuration || 'N/A'],
      ['Ingresos Generados', campaign.revenue || 'N/A'],
      ['Canal', campaign.channel || 'N/A'],
      ['Industria', campaign.industry || 'N/A'],
      ['Fecha de Creación', new Date().toLocaleDateString('es-ES')],
      ['Agente', campaign.agent || 'VoiceBot Pro']
    ];

    // Add UTF-8 BOM for proper Excel compatibility
    const csvContent = '\uFEFF' + csvData.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const filename = `campana_${campaign.name.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
    return downloadFile(csvContent, filename, 'text/csv;charset=utf-8');
  };

  const exportToJson = (campaign) => {
    const jsonData = {
      campaignInfo: {
        name: campaign.name,
        type: getTypeLabel(campaign.type),
        status: campaign.status,
        agent: campaign.agent || 'VoiceBot Pro',
        industry: campaign.industry || 'N/A',
        channel: campaign.channel || 'N/A'
      },
      metrics: {
        totalContacts: campaign.contacts || 0,
        completedContacts: campaign.completed || 0,
        progress: campaign.progress || 0,
        successRate: campaign.successRate || 0,
        avgDuration: campaign.avgDuration || 'N/A',
        revenue: campaign.revenue || 'N/A'
      },
      exportInfo: {
        exportedAt: new Date().toISOString(),
        exportedBy: 'Amb Tu Platform',
        version: '1.0',
        locale: 'es-ES'
      }
    };

    const jsonContent = JSON.stringify(jsonData, null, 2);
    const filename = `campana_${campaign.name.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
    return downloadFile(jsonContent, filename, 'application/json;charset=utf-8');
  };

  const [showExportOptions, setShowExportOptions] = useState(false);

  const handleExport = (format = 'csv') => {
    let downloadSuccess = false;
    
    try {
      if (format === 'json') {
        downloadSuccess = exportToJson(campaign);
        console.log(`Intentando exportar campaña: ${campaign.name} a JSON`);
      } else {
        downloadSuccess = exportToCsv(campaign);
        console.log(`Intentando exportar campaña: ${campaign.name} a CSV`);
      }
      
      // Mostrar mensaje basado en el resultado
      const successMsg = document.createElement('div');
      if (downloadSuccess) {
        successMsg.textContent = `✅ Campaña "${campaign.name}" exportada exitosamente (${format.toUpperCase()})`;
        successMsg.style.cssText = `
          position: fixed; top: 20px; right: 20px; z-index: 10000;
          background: #22c55e; color: white; padding: 12px 20px;
          border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          font-weight: 500; font-size: 14px; max-width: 400px;
        `;
      } else {
        // Fallback: Show content in new window if download fails
        const content = format === 'json' ? 
          JSON.stringify({
            campaignInfo: { name: campaign.name, type: getTypeLabel(campaign.type) },
            message: 'Exportación manual - copia este contenido'
          }, null, 2) :
          `Nombre,${campaign.name}\nTipo,${getTypeLabel(campaign.type)}\nEstado,${campaign.status}`;
          
        const newWindow = window.open('', '_blank');
        newWindow.document.write(`
          <html>
            <head><title>Exportación Manual - ${campaign.name}</title></head>
            <body style="font-family: monospace; padding: 20px;">
              <h3>Exportación Manual - ${campaign.name}</h3>
              <p>Copia el siguiente contenido y guárdalo como archivo .${format}:</p>
              <textarea style="width: 100%; height: 400px; font-family: monospace;">${content}</textarea>
            </body>
          </html>
        `);
        
        successMsg.textContent = `⚠️ Descarga automática no disponible. Se abrió ventana para copia manual.`;
        successMsg.style.cssText = `
          position: fixed; top: 20px; right: 20px; z-index: 10000;
          background: #f59e0b; color: white; padding: 12px 20px;
          border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          font-weight: 500; font-size: 14px; max-width: 400px;
        `;
      }
      
      document.body.appendChild(successMsg);
      setTimeout(() => {
        if (document.body.contains(successMsg)) {
          document.body.removeChild(successMsg);
        }
      }, 5000);
      
      setShowExportOptions(false);
    } catch (error) {
      console.error('Error exportando campaña:', error);
      
      // Show error message
      const errorMsg = document.createElement('div');
      errorMsg.textContent = `❌ Error al exportar la campaña. Detalles: ${error.message}`;
      errorMsg.style.cssText = `
        position: fixed; top: 20px; right: 20px; z-index: 10000;
        background: #ef4444; color: white; padding: 12px 20px;
        border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        font-weight: 500; font-size: 14px; max-width: 400px;
      `;
      document.body.appendChild(errorMsg);
      setTimeout(() => {
        if (document.body.contains(errorMsg)) {
          document.body.removeChild(errorMsg);
        }
      }, 5000);
    }
  };
  const [activeMetricTab, setActiveMetricTab] = useState('overview');

  if (!isOpen || !campaign) return null;

  // Campaign-specific Looker tabs based on campaign type
  const getCampaignLookerTabs = () => {
    // Usar la URL específica de la campaña si existe, sino usar la URL del cliente, sino usar una URL por defecto
    const campaignSpecificUrl = campaign.looker_dashboard_url;
    // Si el campo es un JSON de URLs por pestaña, parsearlo
    let perTabUrls = null;
    if (typeof campaignSpecificUrl === 'string' && campaignSpecificUrl.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(campaignSpecificUrl);
        // Dos formatos posibles: { 'general-metrics': '...' } o { looker_dashboard_urls: { ... } }
        perTabUrls = parsed.looker_dashboard_urls || parsed;
      } catch (e) {
        perTabUrls = null;
      }
    }
    const clientSpecificUrl = clientConfig?.looker_dashboard_url;
    const baseUrl = campaignSpecificUrl || clientSpecificUrl || 'https://lookerstudio.google.com/embed/reporting/383eb531-c54f-4e82-b53c-4760be003fcc/page/qgR';

    // Si la campaña define URLs por pestaña, mapear a 4 pestañas estándar
    if (perTabUrls && Object.keys(perTabUrls).length > 0) {
      return [
        {
          id: 'general-metrics',
          name: 'Métricas Generales',
          description: 'Dashboard específico de Métriques Generals',
          icon: BarChart3,
          url: perTabUrls['general-metrics'] || clientSpecificUrl || baseUrl
        },
        {
          id: 'temporal-analysis',
          name: 'Análisis Temporal',
          description: 'Dashboard específico de Anàlisi Temporal',
          icon: Activity,
          url: perTabUrls['temporal-analysis'] || clientSpecificUrl || baseUrl
        },
        {
          id: 'ddi-campaigns',
          name: 'Campañas DDI',
          description: 'Dashboard específico de Campanyes DDI',
          icon: MessageSquare,
          url: perTabUrls['ddi-campaigns'] || clientSpecificUrl || baseUrl
        },
        {
          id: 'conversion-kpis',
          name: 'Conversión y KPIs',
          description: 'Dashboard específico de Conversió i KPIs',
          icon: Target,
          url: perTabUrls['conversion-kpis'] || clientSpecificUrl || baseUrl
        }
      ];
    }

    // Si la campaña tiene una URL única (string no-JSON), usar solo esa pestaña
    if (campaignSpecificUrl && !perTabUrls) {
      return [
        {
          id: 'campaign-dashboard',
          name: 'Dashboard de Campaña',
          description: `Dashboard personalizado para ${campaign.name}`,
          icon: BarChart3,
          url: campaignSpecificUrl
        }
      ];
    }

    // Si el cliente tiene una URL específica, usar esa como base
    if (clientSpecificUrl) {
      return [
        {
          id: 'client-dashboard',
          name: 'Dashboard del Cliente',
          description: `Dashboard personalizado para ${clientConfig.name}`,
          icon: BarChart3,
          url: clientSpecificUrl
        }
      ];
    }

    // Si no hay URL específica, usar tabs basados en el tipo de campaña
    switch (campaign.type) {
      case 'sales':
        return [
          {
            id: 'sales-funnel',
            name: 'Embudo de Ventas',
            description: 'Análisis del proceso de conversión de leads',
            icon: Target,
            url: `${baseUrl}?campaign=${campaign.id}&type=sales-funnel`
          },
          {
            id: 'performance',
            name: 'Rendimiento de Ventas',
            description: 'KPIs y métricas de performance de ventas',
            icon: TrendingUp,
            url: `${baseUrl}?campaign=${campaign.id}&type=performance`
          },
          {
            id: 'geographic',
            name: 'Distribución Geográfica',
            description: 'Análisis de ventas por ubicación geográfica',
            icon: MapPin,
            url: `${baseUrl}?campaign=${campaign.id}&type=geographic`
          }
        ];
      case 'collections':
        return [
          {
            id: 'recovery-rates',
            name: 'Tasas de Recuperación',
            description: 'Análisis de efectividad en cobranza',
            icon: Target,
            url: `${baseUrl}?campaign=${campaign.id}&type=recovery-rates`
          },
          {
            id: 'payment-patterns',
            name: 'Patrones de Pago',
            description: 'Análisis de comportamiento de pago',
            icon: BarChart3,
            url: `${baseUrl}?campaign=${campaign.id}&type=payment-patterns`
          },
          {
            id: 'contact-optimization',
            name: 'Optimización de Contacto',
            description: 'Mejores horarios y canales de contacto',
            icon: Clock,
            url: `${baseUrl}?campaign=${campaign.id}&type=contact-optimization`
          }
        ];
      case 'retention':
        return [
          {
            id: 'retention-metrics',
            name: 'Métricas de Retención',
            description: 'Análisis de retención de clientes',
            icon: Users,
            url: `${baseUrl}?campaign=${campaign.id}&type=retention-metrics`
          },
          {
            id: 'churn-analysis',
            name: 'Análisis de Abandono',
            description: 'Factores que influyen en el churn',
            icon: AlertCircle,
            url: `${baseUrl}?campaign=${campaign.id}&type=churn-analysis`
          },
          {
            id: 'loyalty-insights',
            name: 'Insights de Lealtad',
            description: 'Análisis de programa de lealtad',
            icon: Activity,
            url: `${baseUrl}?campaign=${campaign.id}&type=loyalty-insights`
          }
        ];
      default:
        return [
          {
            id: 'general-metrics',
            name: 'Métricas Generales',
            description: 'Vista general del rendimiento de la campaña',
            icon: BarChart3,
            url: `${baseUrl}?campaign=${campaign.id}&type=general-metrics`
          },
          {
            id: 'contact-analysis',
            name: 'Análisis de Contactos',
            description: 'Análisis detallado de contactos realizados',
            icon: Phone,
            url: `${baseUrl}?campaign=${campaign.id}&type=contact-analysis`
          },
          {
            id: 'time-analysis',
            name: 'Análisis Temporal',
            description: 'Patrones temporales de la campaña',
            icon: Clock,
            url: `${baseUrl}?campaign=${campaign.id}&type=time-analysis`
          }
        ];
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#34d399'; // Verde más brillante
      case 'paused': return '#fbbf24'; // Amarillo más brillante
      case 'completed': return '#9ca3af'; // Gris más claro
      default: return '#94a3b8'; // Gris claro por defecto
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'sales': return '#22c55e';
      case 'collections': return '#f59e0b';
      case 'retention': return '#8b5cf6';
      case 'surveys': return '#3b82f6';
      case 'appointments': return '#06b6d4';
      default: return '#64748b';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      sales: 'Ventas',
      collections: 'Cobranzas',
      retention: 'Retención',
      surveys: 'Encuestas',
      appointments: 'Citas',
      support: 'Soporte'
    };
    return labels[type] || type;
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'voicebot': return Activity;
      case 'chatbot': return Zap;
      case 'human': return Users;
      case 'hybrid': return Share2;
      default: return Phone;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()} key={`modal-${currentLanguage}-${renderKey}`}>
        
        {/* Header */}
        <div className="modal-header">
          <div className="header-content">
            <div className="campaign-info">
              <h1 className="campaign-title">{campaign.name}</h1>
              <div className="campaign-meta">
                <span 
                  className="campaign-type"
                  style={{ backgroundColor: getTypeColor(campaign.type) }}
                >
                  {getTypeLabel(campaign.type)}
                </span>
                <span 
                  className="campaign-status"
                  style={{ color: getStatusColor(campaign.status) }}
                >
                  {campaign.status === 'active' ? t('campaignDetails.status.active') : 
                   campaign.status === 'paused' ? t('campaignDetails.status.paused') : t('campaignDetails.status.completed')}
                </span>
                <span className="campaign-agent">
                  {React.createElement(getChannelIcon(campaign.channel), { size: 16 })}
                  {campaign.agent}
                </span>
              </div>
            </div>
            
            <div className="header-actions">
              <button 
                className="action-btn secondary"
                onClick={() => {
                  console.log(`Editando campaña: ${campaign.name}`);
                  // Aquí se implementaría la lógica de edición
                  alert(`Función de editar campaña: ${campaign.name}`);
                }}
              >
                <Edit size={16} />
                {t('campaignDetails.edit')}
              </button>
              <button 
                className="action-btn secondary"
                onClick={() => {
                  console.log(`Duplicando campaña: ${campaign.name}`);
                  // Aquí se implementaría la lógica de duplicación
                  alert(`Función de duplicar campaña: ${campaign.name}`);
                }}
              >
                <Copy size={16} />
                {t('campaignDetails.duplicate')}
              </button>
              <div className="export-container" style={{ position: 'relative' }}>
                <button 
                  className="action-btn secondary"
                  onClick={() => setShowExportOptions(!showExportOptions)}
                >
                  <Download size={16} />
                  {t('campaignDetails.export')}
                </button>
                
                {showExportOptions && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: '0',
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '180px',
                    marginTop: '4px'
                  }}>
                    <button
                      onClick={() => handleExport('csv')}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        borderBottom: '1px solid #f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#f8fafc'}
                      onMouseOut={(e) => e.target.style.background = 'none'}
                    >
                      📄 Descargar CSV
                    </button>
                    <button
                      onClick={() => handleExport('json')}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        borderBottom: '1px solid #f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#f8fafc'}
                      onMouseOut={(e) => e.target.style.background = 'none'}
                    >
                      📋 Descargar JSON
                    </button>
                    <button
                      onClick={() => {
                        // Always use the manual copy method as reliable alternative
                        const csvContent = `"Campo","Valor"
"Nombre de Campaña","${campaign.name}"
"Tipo","${getTypeLabel(campaign.type)}"
"Estado","${campaign.status}"
"Contactos Totales","${campaign.contacts || 0}"
"Contactos Realizados","${campaign.completed || 0}"
"Progreso","${campaign.progress || 0}%"
"Tasa de Éxito","${campaign.successRate || 0}%"
"Duración Promedio","${campaign.avgDuration || 'N/A'}"
"Ingresos Generados","${campaign.revenue || 'N/A'}"
"Canal","${campaign.channel || 'N/A'}"
"Industria","${campaign.industry || 'N/A'}"
"Fecha de Creación","${new Date().toLocaleDateString('es-ES')}"
"Agente","${campaign.agent || 'VoiceBot Pro'}"`;

                        const newWindow = window.open('', '_blank', 'width=800,height=600');
                        newWindow.document.write(`
                          <html>
                            <head>
                              <title>Datos de Campaña - ${campaign.name}</title>
                              <style>
                                body { font-family: Arial, sans-serif; padding: 20px; background: #f5f5f5; }
                                .container { background: white; padding: 30px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
                                h2 { color: #00CED1; margin-bottom: 20px; }
                                textarea { width: 100%; height: 300px; padding: 15px; border: 1px solid #ddd; border-radius: 4px; font-family: monospace; font-size: 13px; }
                                .instructions { background: #e6f7ff; padding: 15px; border-radius: 4px; margin-bottom: 20px; border-left: 4px solid #00CED1; }
                                .copy-btn { background: #00CED1; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; margin-top: 10px; }
                                .copy-btn:hover { background: #00a0a0; }
                              </style>
                            </head>
                            <body>
                              <div class="container">
                                <h2>📊 Datos de Campaña: ${campaign.name}</h2>
                                <div class="instructions">
                                  <strong>📋 Instrucciones:</strong><br>
                                  1. Selecciona todo el contenido del cuadro de texto (Ctrl+A)<br>
                                  2. Copia el contenido (Ctrl+C)<br>
                                  3. Pégalo en un nuevo archivo y guárdalo como <strong>${campaign.name.replace(/[^a-zA-Z0-9]/g, '_')}.csv</strong>
                                </div>
                                <textarea id="csvData" readonly>${csvContent}</textarea>
                                <button class="copy-btn" onclick="
                                  const textarea = document.getElementById('csvData');
                                  textarea.select();
                                  document.execCommand('copy');
                                  this.textContent = '✅ Copiado al portapapeles';
                                  setTimeout(() => this.textContent = '📋 Copiar todo', 2000);
                                ">📋 Copiar todo</button>
                              </div>
                            </body>
                          </html>
                        `);
                        
                        setShowExportOptions(false);
                        
                        // Show success message
                        const successMsg = document.createElement('div');
                        successMsg.textContent = `✅ Ventana de exportación abierta. Copia el contenido desde la nueva pestaña.`;
                        successMsg.style.cssText = `
                          position: fixed; top: 20px; right: 20px; z-index: 10000;
                          background: #00CED1; color: white; padding: 12px 20px;
                          border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                          font-weight: 500; font-size: 14px; max-width: 400px;
                        `;
                        document.body.appendChild(successMsg);
                        setTimeout(() => {
                          if (document.body.contains(successMsg)) {
                            document.body.removeChild(successMsg);
                          }
                        }, 5000);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#f8fafc'}
                      onMouseOut={(e) => e.target.style.background = 'none'}
                    >
                      🪟 Abrir en ventana nueva
                    </button>
                  </div>
                )}
              </div>
              <button className="close-btn" onClick={onClose}>
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="progress-section">
            <div className="progress-info">
              <span>{t('campaignMgmt.progress')}: {campaign.completed?.toLocaleString() || 0} / {campaign.contacts?.toLocaleString() || 0}</span>
              <span>{campaign.progress || 0}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${campaign.progress || 0}%`,
                  backgroundColor: getTypeColor(campaign.type)
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* KPIs Grid */}
        <div className="kpis-grid">
          <div className="kpi-card primary">
            <div className="kpi-icon">
              <Users size={24} />
            </div>
            <div className="kpi-content">
              <div className="kpi-value">{campaign.contacts?.toLocaleString() || '0'}</div>
              <div className="kpi-label">Contactos Totales</div>
            </div>
            <div className="kpi-trend positive">+12%</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">
              <Phone size={24} />
            </div>
            <div className="kpi-content">
              <div className="kpi-value">{campaign.completed?.toLocaleString() || '0'}</div>
              <div className="kpi-label">Contactos Realizados</div>
            </div>
            <div className="kpi-trend positive">+8%</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">
              <Target size={24} />
            </div>
            <div className="kpi-content">
              <div className="kpi-value">{campaign.success_rate || 0}%</div>
              <div className="kpi-label">Tasa de Éxito</div>
            </div>
            <div className="kpi-trend positive">+3%</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">
              <Clock size={24} />
            </div>
            <div className="kpi-content">
              <div className="kpi-value">{campaign.kpis?.avg_duration || '0m'}</div>
              <div className="kpi-label">Duración Promedio</div>
            </div>
            <div className="kpi-trend neutral">+0.2%</div>
          </div>

          {campaign.revenue > 0 && (
            <div className="kpi-card success">
              <div className="kpi-icon">
                <TrendingUp size={24} />
              </div>
              <div className="kpi-content">
                <div className="kpi-value">€{(campaign.revenue / 1000).toFixed(0)}K</div>
                <div className="kpi-label">Ingresos Generados</div>
              </div>
              <div className="kpi-trend positive">+15%</div>
            </div>
          )}
        </div>

        {/* Campaign Controls */}
        <div className="campaign-controls">
          {campaign.status === 'active' && (
            <button 
              className="control-btn pause"
              onClick={() => {
                console.log(`Pausando campaña: ${campaign.name}`);
                alert(`Función de pausar campaña: ${campaign.name}`);
              }}
            >
              <Pause size={16} />
              {t('campaignDetails.pauseCampaign')}
            </button>
          )}
          {campaign.status === 'paused' && (
            <button 
              className="control-btn play"
              onClick={() => {
                console.log(`Reanudando campaña: ${campaign.name}`);
                alert(`Función de reanudar campaña: ${campaign.name}`);
              }}
            >
              <Play size={16} />
              {t('campaignDetails.resumeCampaign')}
            </button>
          )}
          <button 
            className="control-btn secondary"
            onClick={() => {
              console.log(`Configurando campaña: ${campaign.name}`);
              alert(`Función de configurar campaña: ${campaign.name}`);
            }}
          >
            <Settings size={16} />
            {t('campaignDetails.configure')}
          </button>
          <button 
            className="control-btn secondary"
            onClick={() => {
              console.log(`Actualizando datos de campaña: ${campaign.name}`);
              alert(`Función de actualizar datos: ${campaign.name}`);
              // Aquí se podría implementar una recarga de datos
            }}
          >
            <RefreshCw size={16} />
            {t('campaignDetails.refreshData')}
          </button>
        </div>

        {/* Looker Dashboards Section */}
        <div className="looker-section">
          <div className="section-header">
            <h2>{t('campaignDetails.detailedAnalysis')}</h2>
            <p>{t('campaignDetails.interactiveDashboards')}</p>
          </div>
          
          <TabSystem
            tabs={getCampaignLookerTabs()}
            defaultTab={getCampaignLookerTabs()[0]?.id}
            style="modern"
            onTabChange={(tabId) => console.log(`Changed to tab: ${tabId}`)}
            showIframeControls={true}
            className="campaign-tabs"
          />
        </div>

      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          overflow-y: auto;
        }

        .modal-container {
          background: white;
          border-radius: 20px;
          max-width: 1200px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
          position: relative;
        }

        .modal-header {
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          color: white;
          padding: 32px;
          border-radius: 20px 20px 0 0;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
        }

        .campaign-title {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 12px;
          line-height: 1.2;
          color: white !important;
        }

        .campaign-meta {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .campaign-type {
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .campaign-status {
          font-size: 14px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
          color: white; /* Respaldo si el color dinámico no funciona bien */
        }

        .campaign-agent {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          opacity: 0.9;
          color: white;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
        }

        .action-btn.secondary {
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .action-btn.secondary:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-1px);
        }

        .close-btn {
          padding: 8px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: scale(1.05);
        }

        .progress-section {
          background: rgba(255, 255, 255, 0.1);
          padding: 20px;
          border-radius: 12px;
          margin-top: 16px;
        }

        .progress-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          font-size: 14px;
          font-weight: 500;
          color: white;
        }

        .progress-bar {
          height: 8px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          transition: width 0.3s ease;
          background: white;
        }

        .kpis-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          padding: 32px;
          background: #f8fafc;
        }

        .kpi-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid #f1f5f9;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .kpi-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: #e2e8f0;
        }

        .kpi-card.primary::before {
          background: linear-gradient(90deg, #00CED1, #48CAE4);
        }

        .kpi-card.success::before {
          background: linear-gradient(90deg, #22c55e, #34d399);
        }

        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .kpi-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #00CED1, #48CAE4);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 16px;
        }

        .kpi-card.success .kpi-icon {
          background: linear-gradient(135deg, #22c55e, #34d399);
        }

        .kpi-value {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          line-height: 1;
          margin-bottom: 8px;
        }

        .kpi-label {
          font-size: 14px;
          color: #64748b;
          font-weight: 500;
        }

        .kpi-trend {
          position: absolute;
          top: 20px;
          right: 20px;
          font-size: 12px;
          font-weight: 600;
          padding: 4px 8px;
          border-radius: 12px;
        }

        .kpi-trend.positive {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        }

        .kpi-trend.negative {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .kpi-trend.neutral {
          background: rgba(100, 116, 139, 0.1);
          color: #64748b;
        }

        .campaign-controls {
          display: flex;
          gap: 16px;
          padding: 0 32px 32px 32px;
          background: #f8fafc;
        }

        .control-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
        }

        .control-btn.pause {
          background: linear-gradient(135deg, #f59e0b, #fbbf24);
          color: white;
        }

        .control-btn.play {
          background: linear-gradient(135deg, #22c55e, #34d399);
          color: white;
        }

        .control-btn.secondary {
          background: white;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }

        .control-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .looker-section {
          padding: 32px;
          background: white;
        }

        .section-header {
          margin-bottom: 24px;
        }

        .section-header h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 8px;
        }

        .section-header p {
          font-size: 16px;
          color: #64748b;
          margin: 0;
        }

        .campaign-tabs {
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #f1f5f9;
        }

        @media (max-width: 768px) {
          .modal-container {
            margin: 10px;
            max-height: 95vh;
          }

          .modal-header {
            padding: 20px;
          }

          .campaign-title {
            font-size: 22px;
            color: white !important;
          }

          .header-content {
            flex-direction: column;
            gap: 16px;
          }

          .header-actions {
            align-self: flex-end;
          }

          .kpis-grid {
            grid-template-columns: 1fr;
            padding: 20px;
          }

          .campaign-controls {
            flex-direction: column;
            padding: 0 20px 20px 20px;
          }

          .looker-section {
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default CampaignDetailsModal;